import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * useSchedule
 * - Fetches schedule ONCE when slideshow starts (initial fetch only)
 * - Schedule contains SubPlan with start_date/end_date/start_time/end_time
 * - No need to re-fetch schedule - SubPlan validation handles time ranges
 * - Sends periodic heartbeat to keep viewing session alive (prevents cleanup)
 * - Only re-fetches on manual refetch({ force: true }) or error recovery
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const useSchedule = (subscriptionId, scheduleId, email, isSubmitted, onError, showDashboard = false) => {
  const [schedule, setSchedule] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [lastError, setLastError] = useState(null);
  // Set when the heartbeat is rejected with 401: 'SESSION_KILLED' (admin) or 'UNAUTHORIZED'.
  // The heartbeat doubles as the kill check, so the viewer needs no separate polling.
  const [sessionEnded, setSessionEnded] = useState(null);

  const previousScheduleRef = useRef(null);
  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(false);
  const scheduleFetchedRef = useRef(false); // Track if schedule was already fetched

  // Backoff management
  const errorCountRef = useRef(0);

  // Env-configurable behavior
  const heartbeatIntervalMs = parseInt(process.env.REACT_APP_HEARTBEAT_INTERVAL_MS || '60000', 10); // 60s heartbeat
  const maxIntervalMs = parseInt(process.env.REACT_APP_SCHEDULE_MAX_REFRESH_INTERVAL_MS || '300000', 10);
  const jitterMs = parseInt(process.env.REACT_APP_SCHEDULE_JITTER_MS || '3000', 10);
  const pauseOnHidden = (process.env.REACT_APP_SCHEDULE_VISIBILITY_PAUSE || '1') === '1';

  const computeNextDelay = useCallback((hasError) => {
    if (!hasError) {
      errorCountRef.current = 0;
      return heartbeatIntervalMs + Math.floor(Math.random() * jitterMs);
    }
    // Exponential backoff: min(base * 2^errors, max) + jitter
    const backoff = Math.min(heartbeatIntervalMs * Math.pow(2, errorCountRef.current), maxIntervalMs);
    return backoff + Math.floor(Math.random() * jitterMs);
  }, [heartbeatIntervalMs, maxIntervalMs, jitterMs]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const cancelInFlight = () => {
    try {
      controllerRef.current?.abort();
    } catch (_) {
      // ignore
    } finally {
      controllerRef.current = null;
    }
  };

  const fetchSchedule = useCallback(
    async (opts = { force: false }) => {
      if (!mountedRef.current) return;
      if (!isSubmitted || !subscriptionId || !email) return;
      
      // Don't fetch schedule when Dashboard is shown (only fetch during slideshow)
      if (showDashboard && !opts.force) {
        clearTimer();
        return;
      }

      // If schedule already fetched and not forcing, skip and use heartbeat instead
      if (scheduleFetchedRef.current && !opts.force) {
        console.log('✅ [SCHEDULE] Already fetched, using heartbeat mode');
        const delay = computeNextDelay(false);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
        return;
      }

      // Respect page visibility (optional)
      if (pauseOnHidden && typeof document !== 'undefined' && document.hidden && !opts.force) {
        const delay = computeNextDelay(false);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
        return;
      }

      // Cancel any in-flight request
      cancelInFlight();
      const controller = new AbortController();
      controllerRef.current = controller;

      // Build URL with subscriptionId and scheduleId
      let url = `${API_BASE}/api/schedule/v2?subscriptionId=${encodeURIComponent(subscriptionId)}`;
      if (scheduleId) {
        url += `&scheduleId=${encodeURIComponent(scheduleId)}`;
      }
      
      // Add clientSessionId for concurrent session detection
      const clientSessionId = sessionStorage.getItem('mustrust_client_session_id');
      if (clientSessionId) {
        url += `&clientSessionId=${encodeURIComponent(clientSessionId)}`;
      }

      setIsFetching(true);
      setLastError(null);

      try {
        console.log(`📡 [SCHEDULE] Fetching schedule from MusTrusT for subscriptionId: ${subscriptionId}`);
        
        const res = await axios.get(
          url,
          {
            signal: controller.signal,
            withCredentials: true, // Send session cookie
          }
        );

        const newSchedule = res.data ?? null;

        // Only update if data changed
        const prev = previousScheduleRef.current;
        const changed = JSON.stringify(prev) !== JSON.stringify(newSchedule);

        if (changed) {
          setSchedule(newSchedule);
          previousScheduleRef.current = newSchedule;
          console.log(`✅ [SCHEDULE] Schedule fetched and updated - subscriptionId: ${subscriptionId}`);
        } else {
          console.log(`ℹ️ [SCHEDULE] Schedule unchanged - subscriptionId: ${subscriptionId}`);
        }

        setLastFetchedAt(new Date());
        errorCountRef.current = 0;
        
        // Mark as fetched - switch to heartbeat mode for subsequent calls
        scheduleFetchedRef.current = true;
        
        // Schedule heartbeat instead of re-fetching
        const delay = computeNextDelay(false);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
        
      } catch (err) {
        if (axios.isCancel?.(err)) {
          // Request aborted due to unmount or param change; don't treat as error
          return;
        }

        console.error(`❌ [SCHEDULE] Failed to fetch schedule:`, err.message);

        // Surface to caller (App handles 403 etc.)
        setLastError(err);
        onError?.(err);

        errorCountRef.current += 1;
        const delay = computeNextDelay(true);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
        setIsFetching(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionId, email, isSubmitted, pauseOnHidden, showDashboard, scheduleId, computeNextDelay]
  );

  /**
   * Send lightweight heartbeat to keep viewing session alive
   * This is much more efficient than re-fetching entire schedule
   */
  const sendHeartbeat = useCallback(
    async () => {
      if (!mountedRef.current) return;
      if (!isSubmitted || !subscriptionId || !email) return;
      
      // Don't send heartbeat when Dashboard is shown (only during slideshow)
      if (showDashboard) {
        clearTimer();
        return;
      }

      // Respect page visibility (optional)
      if (pauseOnHidden && typeof document !== 'undefined' && document.hidden) {
        const delay = computeNextDelay(false);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
        return;
      }

      const clientSessionId = sessionStorage.getItem('mustrust_client_session_id');
      if (!clientSessionId) {
        console.warn('⚠️ [HEARTBEAT] No clientSessionId found');
        const delay = computeNextDelay(true);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
        return;
      }

      try {
        await axios.post(
          `${API_BASE}/api/viewing/heartbeat`,
          {
            subscriptionId,
            clientSessionId
          },
          {
            withCredentials: true, // Send session cookie
          }
        );

        console.log(`💓 [HEARTBEAT] Success - subscriptionId: ${subscriptionId}`);
        errorCountRef.current = 0;
        const delay = computeNextDelay(false);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
      } catch (err) {
        console.error('❌ [HEARTBEAT] Failed:', err.message);

        // Session killed by an admin or expired: stop the heartbeat and let the viewer say so
        if (err.response?.status === 401) {
          clearTimer();
          setSessionEnded(err.response.data?.error || 'UNAUTHORIZED');
          return;
        }

        // If session not found (404), try fetching schedule again
        if (err.response?.status === 404) {
          console.log('🔄 [HEARTBEAT] Session not found, re-fetching schedule...');
          scheduleFetchedRef.current = false; // Reset flag to allow re-fetch
          fetchSchedule().catch((e) => console.error('fetchSchedule error:', e));
          return;
        }

        errorCountRef.current += 1;
        const delay = computeNextDelay(true);
        clearTimer();
        timerRef.current = setTimeout(() => {
          sendHeartbeat().catch((e) => console.error('sendHeartbeat error:', e));
        }, delay);
      }
    },
    [subscriptionId, email, isSubmitted, pauseOnHidden, showDashboard, computeNextDelay, fetchSchedule]
  );

  const fetchScheduleSafe = useCallback(() => {
    fetchSchedule().catch((e) => {
      console.error('fetchScheduleSafe error:', e);
    });
  }, [fetchSchedule]);

  // Lifecycle: handle mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      cancelInFlight();
    };
  }, []);

  // Start/stop polling when subscription/email/submission status changes
  useEffect(() => {
    clearTimer();
    cancelInFlight();

    // Reset prev schedule when subscription/email changes
    previousScheduleRef.current = null;
    errorCountRef.current = 0;
    scheduleFetchedRef.current = false; // Reset fetch flag for new subscription

    if (!isSubmitted || !subscriptionId || !email) {
      return;
    }
    
    // Don't fetch if Dashboard is shown
    if (showDashboard) {
      return;
    }

    // Immediate fetch on first load, then switch to heartbeat mode
    console.log('🚀 [useSchedule] Triggering initial fetch for:', subscriptionId);
    fetchScheduleSafe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId, email, isSubmitted, showDashboard]);

  // Visibility change handling (optional pause)
  useEffect(() => {
    if (!pauseOnHidden) return;
    const onVis = () => {
      if (!isSubmitted || !subscriptionId || !email) return;
      if (showDashboard) return; // Don't fetch when Dashboard is shown
      // When becoming visible, fetch immediately
      if (!document.hidden) {
        fetchScheduleSafe();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauseOnHidden, subscriptionId, email, isSubmitted, showDashboard]);

  // Public API
  const refetch = useCallback(() => {
    // Force fetch ignoring visibility pause and fetch flag
    // Reset flag to allow re-fetch
    scheduleFetchedRef.current = false;
    clearTimer();
    return fetchSchedule({ force: true });
  }, [fetchSchedule]);

  return { schedule, refetch, lastFetchedAt, isFetching, lastError, sessionEnded };
};
